import { Loader2, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ScopePicker } from "@/components/scope-picker";
import type { ScopeRef } from "@/components/scope-picker/types";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useTenants } from "@/features/tenant-switcher/use-tenants-query";
import {
	ASSIGNABLE_DEVICE_ROLES,
	DEVICE_ROLE_OPTIONS,
	roleAllowsPharmacy,
	roleRequiresPharmacy,
} from "./device-role-config";
import type { AssignableDeviceRole, DeviceAgent } from "./devices-api";
import { useUpdateDeviceAssignmentMutation } from "./use-devices-query";

/** Rola startowa selecta — bieżąca, jeśli edytowalna; inaczej domyślnie apteka. */
function initialRole(device: DeviceAgent): AssignableDeviceRole {
	return ASSIGNABLE_DEVICE_ROLES.has(device.deviceRole)
		? (device.deviceRole as AssignableDeviceRole)
		: "pharmacy_workstation";
}

/** Scope startowy — konkretna apteka, gdy urządzenie ją ma; inaczej cała org. */
function initialScope(device: DeviceAgent): ScopeRef {
	return device.idfirm != null ? { type: "apteka", id: device.idfirm } : { type: "org" };
}

/**
 * Edycja przypisania już zatwierdzonego urządzenia — gdy ktoś pomylił rolę,
 * organizację lub aptekę. Pre-wypełnia bieżące wartości i PATCH-uje. Backend
 * sam czyści idfirm, gdy rola przestaje być apteczna.
 */
export function EditDeviceDialog({
	device,
	open,
	onOpenChange,
}: {
	device: DeviceAgent;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const update = useUpdateDeviceAssignmentMutation();
	const { data: tenants = [] } = useTenants();

	const [role, setRole] = useState<AssignableDeviceRole>(() => initialRole(device));
	const [tenantId, setTenantId] = useState<number | null>(device.tenantId ?? null);
	const [scope, setScope] = useState<ScopeRef>(() => initialScope(device));
	const [displayName, setDisplayName] = useState(device.displayName ?? "");
	// Popover ScopePickera musi portalowac sie WEWNATRZ dialogu, inaczej
	// focus-trap dialogu nie wpuszcza klawiszy do pola szukania (patrz ui/popover).
	const [scopeContainer, setScopeContainer] = useState<HTMLElement | null>(null);

	// Re-seed pól przy otwarciu i zmianie urządzenia — instancja dialogu żyje w
	// kafelku, więc bez tego edytowałoby się na nieaktualnych wartościach.
	// Zależność to deviceUuid, NIE cały obiekt device: lista odpytuje serwer co
	// 15s i każdy refetch daje świeżą referencję device o tych samych danych —
	// gdyby wisiała tu na deps, kasowałaby wpisany, niezapisany tekst.
	// biome-ignore lint/correctness/useExhaustiveDependencies: seed świadomie tylko po (open, deviceUuid); device czytany celowo bez śledzenia referencji.
	useEffect(() => {
		if (!open) return;
		setRole(initialRole(device));
		setTenantId(device.tenantId ?? null);
		setScope(initialScope(device));
		setDisplayName(device.displayName ?? "");
	}, [open, device.deviceUuid]);

	const requiresPharmacy = roleRequiresPharmacy(role);
	const allowsPharmacy = roleAllowsPharmacy(role);
	const idfirm = scope.type === "apteka" ? scope.id : null;
	const canSubmit = tenantId != null && (!requiresPharmacy || idfirm != null) && !update.isPending;

	function handleSubmit() {
		if (tenantId == null) {
			toast.error("Wybierz organizację");
			return;
		}
		if (requiresPharmacy && idfirm == null) {
			toast.error("Dla tej roli wybierz aptekę");
			return;
		}
		update.mutate(
			{
				deviceUuid: device.deviceUuid,
				body: {
					tenantId,
					idfirm: allowsPharmacy ? idfirm : null,
					deviceRole: role,
					displayName: displayName.trim() || null,
				},
			},
			{
				onSuccess: () => {
					toast.success("Zapisano przypisanie urządzenia");
					onOpenChange(false);
				},
				onError: () => toast.error("Nie udało się zapisać przypisania"),
			},
		);
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>Edytuj przypisanie urządzenia</DialogTitle>
					<DialogDescription>
						{device.displayName ?? device.hostname ?? device.deviceUuid} — popraw rolę, organizację
						lub aptekę.
					</DialogDescription>
				</DialogHeader>
				<DialogBody className="space-y-4">
					<div className="space-y-1.5">
						<Label className="text-xs">Rola</Label>
						<Select value={role} onValueChange={(v) => setRole(v as AssignableDeviceRole)}>
							<SelectTrigger className="w-full" aria-label="Rola urządzenia">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{DEVICE_ROLE_OPTIONS.map((o) => (
									<SelectItem key={o.value} value={o.value}>
										{o.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className="space-y-1.5">
						<Label className="text-xs">Organizacja</Label>
						<Select
							value={tenantId != null ? String(tenantId) : ""}
							onValueChange={(v) => setTenantId(Number(v))}
						>
							<SelectTrigger className="w-full" aria-label="Organizacja">
								<SelectValue placeholder="Wybierz organizację" />
							</SelectTrigger>
							<SelectContent>
								{tenants.map((t) => (
									<SelectItem key={t.tenantId} value={String(t.tenantId)}>
										{t.name}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{allowsPharmacy ? (
						<div className="space-y-1.5">
							<Label className="text-xs">Apteka{requiresPharmacy ? "" : " (opcjonalnie)"}</Label>
							<div ref={setScopeContainer}>
								<ScopePicker value={scope} onChange={setScope} portalContainer={scopeContainer} />
							</div>
							{idfirm == null ? (
								<p className="text-[11px] text-muted-foreground">
									Wybierz konkretną aptekę z listy.
								</p>
							) : null}
						</div>
					) : null}

					<div className="space-y-1.5">
						<Label htmlFor="edit-display-name" className="text-xs">
							Nazwa (opcjonalnie)
						</Label>
						<Input
							id="edit-display-name"
							value={displayName}
							onChange={(e) => setDisplayName(e.target.value)}
							placeholder="np. Stanowisko 1 — pierwsze okno"
							maxLength={120}
						/>
					</div>
				</DialogBody>
				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)} disabled={update.isPending}>
						Anuluj
					</Button>
					<Button onClick={handleSubmit} disabled={!canSubmit}>
						{update.isPending ? (
							<Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
						) : (
							<Save className="mr-1.5 h-4 w-4" aria-hidden />
						)}
						Zapisz
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
